// apps/client/src/modules/Shortcuts/ShortcutHint.tsx
import { useEffect, type ReactNode } from 'react'
import { useAtomValue } from 'jotai'
import * as PopperPrimitive from '@radix-ui/react-popper'
import { Portal } from '@radix-ui/react-portal'
import { Kbd, KbdGroup, CmdCtrl, OptAlt } from '@duck/ui'
import { shortcutRegistryAtom, modifierSetMatches } from './ShortcutRegistry'
import { heldModifiersAtom, hintsSuppressedAtom } from './useHeldModifiers'

const KEY_GLYPHS: Record<string, string> = {
  Enter: '↵',
  ArrowLeft: '←',
  ArrowRight: '→',
  ArrowUp: '↑',
  ArrowDown: '↓',
  Backspace: '⌫',
  Escape: 'Esc',
}

const renderKey = (key: string): string => KEY_GLYPHS[key] ?? key.toUpperCase()

interface ShortcutHintProps {
  shortcutId: string
  children: ReactNode
  /** When true, the badge renders only the non-modifier key — modifiers are implicit since the hint only appears while they're held. */
  hideModifiers?: boolean
}

/**
 * Wraps a child element. Renders a centered-above kbd badge when the user is
 * holding the modifiers required by the bound shortcut. Portaled + collision-
 * aware so it escapes `overflow: hidden` ancestors and viewport edges.
 */
export const ShortcutHint = ({ shortcutId, children, hideModifiers }: ShortcutHintProps) => {
  const registry = useAtomValue(shortcutRegistryAtom)
  const held = useAtomValue(heldModifiersAtom)
  const suppressed = useAtomValue(hintsSuppressedAtom)

  const def = registry[shortcutId]

  useEffect(() => {
    if (process.env.NODE_ENV !== 'production' && !def) {
      console.warn(`[ShortcutHint] No shortcut registered for id: "${shortcutId}"`)
    }
  }, [def, shortcutId])

  // Always display the first binding (canonical form). Show the hint when ANY bound modifier
  // set matches what's held — multi-binding shortcuts (e.g. Alt+'+' which on some layouts needs
  // Shift) should hint as soon as the user holds enough modifiers to trigger any variant.
  const bindings = def ? (Array.isArray(def.keys) ? def.keys : [def.keys]) : []
  const binding = bindings[0] ?? null
  const anyMatch = bindings.some((b) => modifierSetMatches(b, held))
  const visible = !!binding && !suppressed && anyMatch

  return (
    <PopperPrimitive.Root>
      <PopperPrimitive.Anchor className="inline-flex">
        {children}
      </PopperPrimitive.Anchor>
      {visible && binding && (
        <Portal>
          <PopperPrimitive.Content
            side="top"
            sideOffset={4}
            collisionPadding={8}
            aria-hidden
            className="pointer-events-none z-[5000]"
          >
            <KbdGroup>
              {!hideModifiers && binding.cmd && <CmdCtrl />}
              {!hideModifiers && binding.alt && <OptAlt />}
              {!hideModifiers && binding.shift && <Kbd>⇧</Kbd>}
              <Kbd>{renderKey(binding.key)}</Kbd>
            </KbdGroup>
          </PopperPrimitive.Content>
        </Portal>
      )}
    </PopperPrimitive.Root>
  )
}
